/**
 * アカウント棚卸について「**何を出すか**」だけを決める（M5f / F-21）。
 *
 * ## ★なぜ DOM から切り離すか
 *
 * 判断を描画関数の中に書くと、
 * **その判断を壊しても `tsc` もテストも落ちない**
 * （`.loop/memory.md`「判断を切り出して検査したら、それを画面へ写す最後の1行が無検査で残る」。
 * M5b では警告欄が画面から丸ごと消えた状態で完了していた）。
 *
 * ★このファイルは **DOM を触らない。** `document` も `el()` も使わない。
 *   そのため node 環境のまま `tests/ui/` から検査できる（jsdom を読み込まない）。
 *
 * ★2026-08-25 / M6c：ここが返したものを DOM へ写す最後の1行
 *   （`accountReviewCard` の `append`）は `tests/ui/main-dom.test.ts` が固定した
 *   （`card.append(list)` を落とす変異で落ちることを実測）。★**閉じたのは「消えないこと」まで。**
 *   幅・折り返し・切れは jsdom では測れないので、そこはブラウザ実測のまま。
 */
import type { Account } from "./api.js";

/** ロールの表示名。★原典 `migrations/0003_auth.sql` の投入値に合わせる */
export const ROLE_LABEL: Record<NonNullable<Account["role"]>, string> = {
  staff: "スタッフ",
  leader: "社員・リーダー",
  manager: "店長",
  owner: "オーナー",
};

/**
 * 一覧の先頭に出す1行。
 *
 * ★`clear` でも**黙らせない。** 「危ないものが無い」と「まだ調べていない」を、
 *   画面上で区別できなくなる（C-14 の `skipped` を `passed` に畳まないのと同じ理由）。
 */
export type AccountReviewBanner = { kind: "risky" | "clear"; text: string };

/** 1件ぶんの表示。描画側はこれを DOM へ写すだけで、出す／出さないを決めない */
export interface AccountRowView {
  loginId: string;
  /** ★`users.display_name`。従業員の氏名（給与情報）ではない */
  headline: string;
  roleLabel: string;
  detail: string;
  /**
   * その行に出すボタン。
   * - `stop` … 止める（確認を挟む）
   * - `restore` … 戻す（安全側なので確認は挟まない）
   * - `none` … 出さない（読むだけの相手・**自分のアカウント**）
   */
  action: "stop" | "restore" | "none";
  /** ★辞めた人がまだ入れる。並び順はサーバが決めているので、ここでは並べ替えない */
  risky: boolean;
  /**
   * ★**いまロックされていて入れない行だけ** `null` 以外（S1 / claude-security F3）。
   *
   * ★`text` に**誰なら解除できるか**まで入れる。
   *   `manager` はロック中の行を見えるが解除できない（解除は owner のみ・原典 §3.5）。
   *   ★**この一文をここに置かないと、店長は「入れない人がいる」と分かった直後に行き止まる**
   *   （`.loop/memory.md`「A が B の操作を禁止された」なら B が最初に触る画面をそのロールで開く。
   *    R-05 で実際に起きた形）。★離れた場所の汎用注記で代用しない。
   */
  lock: { text: string; canUnlock: boolean } | null;
  /**
   * ★**保存されているパスワードが壊れている行だけ** `null` 以外（S5）。
   *
   * ★**`lock` と1つにまとめない。** ロックは時間で切れるが、こちらは**放っておいても直らない。**
   *   同じ欄に出すと「待てば入れる」と読まれ、原因の切り分けが止まる。
   *   ★「ふつうのパスワード間違い」と区別が付かないこと自体が、この課題の起票理由。
   */
  brokenHash: { text: string } | null;
  /** ★一時パスワードを再発行できるか。owner の他人だけ */
  canResetPassword: boolean;
  /**
   * ★ロールを変えられるか（USER-ROLE-CHANGE・`user_admin.write` = owner）。**自分の行は `false`**
   *   （サーバも拒否する。押せる形で置かない——`action` の自分と同じ理由）
   */
  canChangeRole: boolean;
}

/**
 * ★壊れた保存値に気づいたあと、**その場で何ができるか**まで書く（S5 / AC-4）。
 *
 * ★owner はこの画面で再発行でき、manager は owner へ依頼する。
 *   壊れた保存値を見つけた人が、権限に応じた次の行動をその場で判断できる文面にする。
 *
 * ★**開発の識別子を入れない**（`.loop/memory.md`「未実装・未確定の理由を、
 *   利用者向けの文言に開発の識別子のまま書く」）。読むのは店長とオーナー。
 */
/**
 * ★**この一覧が「全部は見つけられない」ことを、常に画面へ出す**（S5 周回2 / spec blocking）。
 *
 * ★**0件は「壊れていない」ではない。** 保存値の壊れ方 24通りのうち、
 *   旗が拾えるのは 17通り（`tests/authz/session.test.ts` の `NOT_DETECTED` に残り7通り）。
 *   ★**「解けない」ではなく「拾えるが置かないと決めた」ものが混ざっている**
 *   （2026-08-24 / 周回3・security の実測。詳細は `src/auth/password.ts`）。
 *
 * ★同じファイルが `clear` について「危ないものが無い」と「まだ調べていない」を
 *   区別しろと書いているのに、**壊れの側は「0件なら黙る」形になっていた。**
 *   開発者向けのコメントにだけ誠実さを書いて、利用者には「ありません」とだけ見せない。
 *
 * ★**旗の有無に関わらず出す。** 旗が立っているときだけ出すと、いちばん危ない状態
 *   （壊れているのに旗が立たない）のときに、この一文も一緒に消える。
 */
/**
 * ★**相談先を役職で変える**（S5 周回3 / spec blocking）。
 *
 * ★**店長は保守の担当者の連絡先を知る手段がシステム内に無い**（保守体制は未回答のまま）。
 *   「保守の担当者へ」とだけ書くと、店長はそこで行き止まる。
 *   原典 §3.5 は同種の状況（店長は気づけるが直せない）で
 *   ★**「復旧は口頭でオーナーへ伝える運用に乗る」**と既に決めている。そちらに合わせる。
 *
 * ★**この出し分けは「誰が直せるか」ではなく「誰が連絡先を知っているか」。**
 *   `BROKEN_HASH_TEXT` は前者の理由で出し分けていなかったが、
 *   ★**2つは別の話**で、同じファイルの `lock` と「止められるのはオーナーのみ」は
 *   どちらも後者の理由で既に出し分けている。ここだけ理由を取り違えていた。
 */
export const brokenHashCaveat = (isOwner: boolean): string =>
  "この一覧は、パスワードの保存値の壊れを、すべては見つけられません。" +
  "旗が出ていなくても「正しいパスワードなのに入れない人がいる」ときは、" +
  (isOwner ? "保守の担当者へ相談してください。" : "オーナーへ伝えてください。");

const brokenHashText = (isOwner: boolean): string =>
  "保存されているパスワードが壊れています。" +
  "この人は、正しいパスワードを入れても入れません。時間がたっても直りません。" +
  (isOwner
    ? "この画面で一時パスワードを再発行してください。"
    : "オーナーへ一時パスワードの再発行を依頼してください。");

export function accountReviewBanner(
  accounts: readonly Account[],
  canWrite: boolean,
): AccountReviewBanner {
  const risky = accounts.filter((a) => a.staffRetiredButActive).length;
  /*
   * ★**退職者とは別に数える**（S5）。同じ数字に足し込むと、
   *   「辞めた人が2件」と「入れない人が1件・辞めた人が1件」が画面で同じ絵になる。
   *   ★件数が0のときは何も足さない（`clear` の文を薄めない）。
   */
  const broken = accounts.filter((a) => a.passwordHashBroken).length;
  const brokenText =
    broken === 0
      ? ""
      : `保存されているパスワードが壊れているアカウントが ${broken}件 あります。` +
        "正しいパスワードでも入れません。下の行を確認してください。";

  if (risky === 0) {
    return {
      // ★壊れた保存値が1件でもあれば `clear` にしない。**「危ないものが無い」ではない**
      kind: broken === 0 ? "clear" : "risky",
      // ★「放置アカウントはありません」と言い切らない。**この一覧の中の話**でしかない
      text: "この一覧に、退職済みの従業員に紐づく有効なアカウントはありません。" + brokenText,
    };
  }
  return {
    kind: "risky",
    text:
      `退職した従業員のアカウントが ${risky}件、まだ有効です。` +
      (canWrite ? "止めるかどうか確認してください。" : "止められるのはオーナーのみです。") +
      brokenText,
  };
}

/**
 * @param viewer.canWrite  止める／戻せるか（`account_review.write` = owner）
 * @param viewer.canUnlock ロックを解除できるか（`user_admin.write` = owner）
 *
 * @param viewer.isOwner   ★**権限ではない。** 保守契約の当事者か（＝連絡先を知っているか）。
 *                          今日は3つとも owner だが、**性質が違うので束ねない**
 *
 * ★**`canWrite` と `canUnlock` を1つにまとめない。** 今日はどちらも owner だが、
 *   原典 §3.5 では別の行で、`account_review` は manager に `R` がある
 *   （`.loop/memory.md`「原典の複数の行を、1つのリソースに束ねない」）。
 */
export function accountRowViews(
  accounts: readonly Account[],
  viewer: { canWrite: boolean; canUnlock: boolean; isOwner: boolean; selfLoginId: string },
): AccountRowView[] {
  return accounts.map((a) => {
    const isSelf = a.loginId === viewer.selfLoginId;
    return {
      loginId: a.loginId,
      headline: `${a.loginId}（${a.displayName}）`,
      // ★ロールが無いアカウントを「—」で流さない。最も気づかれない形なので名指しする
      roleLabel: a.role === null ? "ロール未設定" : ROLE_LABEL[a.role],
      detail:
        `最終ログイン ${a.lastLoginAt ?? "なし"}` +
        ` ／ ${a.isActive ? "有効" : `停止 ${a.retiredOn ?? ""}`}` +
        (a.staffRetiredOn === null ? "" : ` ／ 従業員は ${a.staffRetiredOn} に退職`) +
        (a.staffRetiredButActive ? " ／ 辞めた人がまだ入れます" : "") +
        (a.lockedUntil === null ? "" : " ／ ロック中") +
        (a.mustChangePassword ? " ／ 次回ログインでパスワード変更が必要" : "") +
        (isSelf ? " ／ 自分のアカウント" : ""),
      /*
       * ★自分のボタンは出さない。押しても止まらない（サーバが 400 を返す）が、
       *   **押せる形で置いてあること自体**が「止められるはず」という誤解になる。
       */
      action: !viewer.canWrite || isSelf ? "none" : a.isActive ? "stop" : "restore",
      risky: a.staffRetiredButActive,
      lock:
        a.lockedUntil === null
          ? null
          : {
              text:
                `ログインの失敗が続いたため、いま入れません（${a.lockedUntil} まで）。` +
                (viewer.canUnlock
                  ? "すぐ戻すなら解除してください。"
                  : // ★行き止まりにしない。**誰に頼めばよいか**まで書く
                    "解除できるのはオーナーのみです。待てば時間で解除されます。"),
              canUnlock: viewer.canUnlock,
            },
      brokenHash: a.passwordHashBroken ? { text: brokenHashText(viewer.isOwner) } : null,
      canResetPassword: viewer.isOwner && !isSelf,
      // ★`canUnlock` と同じ `user_admin.write`。原典 §3.5 の同じ行
      canChangeRole: viewer.canUnlock && !isSelf,
    };
  });
}

/**
 * ★時給・個人別人件費を見られるロール（`docs/05` §1）。**判定には使わない。確認文の出し分けだけ。**
 *   権限の根拠は DB の `roles.can_view_payroll`（`src/auth/scope.ts`）。
 */
const SEES_PAYROLL: ReadonlySet<Role> = new Set<Role>(["manager", "owner"]);

type Role = NonNullable<Account["role"]>;

/**
 * ロールを変える前の確認文。★**誰を・何から何へ**と、給与の見え方が変わるときはそれを出す
 *   （manager / owner への変更は、時給を見られる人を増やす操作。`docs/05` §3.5）。
 */
export function roleChangeConfirmText(row: AccountRowView, from: Account["role"], to: Role): string {
  const before = from === null ? "ロール未設定" : ROLE_LABEL[from];
  const gains = SEES_PAYROLL.has(to) && (from === null || !SEES_PAYROLL.has(from));
  const loses = from !== null && SEES_PAYROLL.has(from) && !SEES_PAYROLL.has(to);
  return (
    `${row.headline}のロールを「${before}」から「${ROLE_LABEL[to]}」へ変えます。` +
    (gains ? "変更後は、全員の時給と個人別の人件費が見えるようになります。" : "") +
    (loses ? "変更後は、時給と個人別の人件費が見えなくなります。" : "") +
    "いま開いているログインはその場で切れます。"
  );
}

/** 止める前の確認に出す文言。★誰を止めるのかを、その人だと分かる形で出す */
export const stopConfirmText = (row: AccountRowView): string =>
  `${row.headline}を止めます。いま開いているログインもその場で切れます。あとから戻せます。`;
